import { Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useTrackOnce } from '@/lib/analytics/useTrackOnce';
import { useMyProfile } from '@/hooks/useMyProfile';

import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { FarmlyAvatar } from '@/components/farmly/FarmlyAvatar';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { ArrowLeft, LogOut, Mail, ChevronDown } from 'lucide-react';
import { format } from 'date-fns';
import { sv } from 'date-fns/locale';
import { ProfileIdentitySection } from '@/components/profile/ProfileIdentitySection';

/**
 * Minimal identity + account actions page.
 * Not the primary hub — that's CustomerOverview.
 */
export default function CustomerProfilePage() {
  const { user, roles, rolesLoading, signOut } = useAuth();
  const { profile } = useMyProfile();

  useTrackOnce('mypage_opened', { surface: 'my_page_profile' }, 'profile');

  const userInitials = profile?.display_name
    ? profile.display_name.substring(0, 2).toUpperCase()
    : user?.email 
      ? user.email.substring(0, 2).toUpperCase() 
      : 'AN';

  const handleSignOut = async () => {
    await signOut();
    window.location.href = '/';
  };

  const roleBadge = (role: string) => {
    const labels: Record<string, string> = {
      admin: 'Administratör',
      user: 'Besökare',
      producer: 'Värd',
    };
    return labels[role] || role;
  };

  return (
    <div className="p-6 max-w-2xl mx-auto space-y-6">
      <div className="flex items-center gap-4">
        <Button asChild variant="ghost" size="sm">
          <Link to="/app/customer">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Tillbaka
          </Link>
        </Button>
      </div>

      {/* Identity card */}
      <Card className="border-border/60 shadow-none">
        <CardContent className="p-5 sm:p-6">
          <div className="flex items-center gap-4">
            <FarmlyAvatar
              imageUrl={profile?.avatar_url || user?.user_metadata?.avatar_url}
              initials={userInitials}
              size="lg"
            />
            <div className="flex-1 min-w-0">
              <p className="font-medium text-foreground">
                {profile?.display_name || user?.user_metadata?.full_name || 'Ditt konto'}
              </p>
              <p className="text-sm text-muted-foreground flex items-center gap-1.5 mt-1">
                <Mail className="h-3.5 w-3.5 shrink-0" />
                <span className="truncate">{user?.email}</span>
              </p>
              <div className="flex gap-2 mt-2.5">
                {rolesLoading ? (
                  <Skeleton className="h-5 w-20" />
                ) : (
                  roles.map((role) => (
                    <Badge key={role} variant="secondary" className="text-xs">
                      {roleBadge(role)}
                    </Badge>
                  ))
                )}
              </div>
            </div>
          </div>

          {/* Status line */}
          <p className="text-xs text-muted-foreground/60 mt-4">
            Du är inloggad.
          </p>
        </CardContent>
      </Card>

      {/* Optional identity section */}
      <ProfileIdentitySection />

      {/* Collapsible technical info */}
      <Collapsible>
        <CollapsibleTrigger className="flex w-full items-center justify-between py-2 text-xs font-medium text-muted-foreground/50 hover:text-muted-foreground transition-colors">
          Teknisk info
          <ChevronDown className="h-3 w-3 transition-transform data-[state=open]:rotate-180" />
        </CollapsibleTrigger>
        <CollapsibleContent>
          <Card className="mt-2 border-border/40 shadow-none">
            <CardContent className="p-4 space-y-2 text-xs text-muted-foreground">
              <div className="flex justify-between">
                <span>Konto skapat</span>
                <span className="text-foreground/70">
                  {user?.created_at
                    ? format(new Date(user.created_at), 'd MMMM yyyy', { locale: sv })
                    : '–'}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Senast inloggad</span>
                <span className="text-foreground/70">
                  {user?.last_sign_in_at
                    ? format(new Date(user.last_sign_in_at), 'd MMMM yyyy', { locale: sv })
                    : '–'}
                </span>
              </div>
            </CardContent>
          </Card>
        </CollapsibleContent>
      </Collapsible>

      {/* Sign out */}
      <Button
        variant="outline"
        className="w-full"
        onClick={handleSignOut}
      >
        <LogOut className="mr-2 h-4 w-4" />
        Logga ut
      </Button>
    </div>
  );
}
